import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Star, MapPin, Calendar, DollarSign, Clock, ArrowRight, CheckCircle, Building2 } from 'lucide-react';
import TestimonialModal from './TestimonialModal';

const TestimonialCard = ({ testimonial, featured = false }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${
          i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
      />
    ));
  };

  return (
    <Card className={`group hover:shadow-xl transition-all duration-300 ${
      featured ? 'border-primary/20 shadow-lg' : 'hover:border-primary/30'
    }`}>
      <CardContent className="p-0">
        {/* Header with Client Info */}
        <div className="p-6 pb-4">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="relative">
                <img
                  src={testimonial.clientAvatar}
                  alt={testimonial.clientName}
                  className="w-12 h-12 rounded-full object-cover border-2 border-primary/20"
                />
                {testimonial.verified && (
                  <CheckCircle className="absolute -bottom-1 -right-1 h-4 w-4 text-green-500 bg-white rounded-full" />
                )}
              </div>
              <div>
                <h3 className="font-semibold text-foreground">{testimonial.clientName}</h3>
                <p className="text-sm text-muted-foreground">{testimonial.clientTitle}</p>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Building2 className="h-3 w-3" />
                  <span>{testimonial.clientCompany}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {renderStars(testimonial.rating)}
            </div>
          </div>

          {/* Project Info */}
          <div className="flex flex-wrap gap-2 mb-4">
            <Badge variant="secondary" className="text-xs">
              {testimonial.projectType}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {testimonial.clientIndustry}
            </Badge>
            {featured && (
              <Badge className="text-xs bg-primary text-primary-foreground">
                Featured
              </Badge>
            )}
          </div>

          {/* Location and Date */}
          <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
            <div className="flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              <span>{testimonial.clientLocation}</span>
            </div>
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              <span>{new Date(testimonial.date).toLocaleDateString()}</span>
            </div>
          </div>
        </div>

        {/* Testimonial Content */}
        <div className="px-6 pb-4">
          <blockquote className="text-muted-foreground leading-relaxed mb-4">
            "{testimonial.testimonial}"
          </blockquote>
        </div>

        {/* Project Details */}
        <div className="px-6 pb-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-primary" />
              <span className="text-muted-foreground">Duration:</span>
              <span className="font-medium">{testimonial.projectDuration}</span>
            </div>
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-primary" />
              <span className="text-muted-foreground">Value:</span>
              <span className="font-medium">{testimonial.projectValue}</span>
            </div>
          </div>
        </div>

        {/* Key Results */}
        <div className="px-6 pb-4">
          <h4 className="text-sm font-semibold text-foreground mb-2">Key Results:</h4>
          <div className="space-y-1">
            {testimonial.results.slice(0, 2).map((result, index) => (
              <div key={index} className="flex items-center gap-2 text-xs text-muted-foreground">
                <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                <span>{result}</span>
              </div>
            ))}
            {testimonial.results.length > 2 && (
              <div className="text-xs text-primary font-medium">
                +{testimonial.results.length - 2} more results
              </div>
            )}
          </div>
        </div>

        {/* Tags */}
        <div className="px-6 pb-4">
          <div className="flex flex-wrap gap-1">
            {testimonial.tags.slice(0, 3).map((tag, index) => (
              <Badge key={index} variant="outline" className="text-xs">
                {tag}
              </Badge>
            ))}
            {testimonial.tags.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{testimonial.tags.length - 3}
              </Badge>
            )}
          </div>
        </div>

        {/* Footer with CTA */}
        <div className="px-6 pb-6 pt-2 border-t border-border/50">
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-full group-hover:bg-primary/10 transition-colors"
            onClick={() => setIsModalOpen(true)}
          >
            Read Full Story
            <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </CardContent>

      {/* Modal */}
      <TestimonialModal
        testimonial={testimonial}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </Card>
  );
};

export default TestimonialCard;
